/* eslint-disable max-lines */
/* eslint-disable max-params */
import { readEndpoint, updateResource } from 'redux-json-api';
import { capitalizeMessage, clearMessages, displaySuccessMessage, displayError } from './messagesActions';
import { clearApiState, loadEntities, readLocal, setActivePage } from './coreApiActions';
import { i18n } from '../i18n/translate';
import { loadExternalData } from '../utils/ApiUtil';
import { buildUrl } from '../json-api/JsonApiUtils';
import { cloneDeep } from 'lodash';

export const readEntity = (
  entity,
  includes = '',
  filters = {},
  sorts = [],
  page = {},
  query = {},
  clearState = true,
  entityAlias = undefined,
  skipJSONExtension = false
) => {
  if (typeof entity === 'object') {
    return readEntityFromObject(entity);
  } else {
    return readEntityFromParams(
      entity,
      includes,
      filters,
      sorts,
      page,
      query,
      clearState,
      entityAlias,
      skipJSONExtension
    );
  }
};

const readEntityFromParams = (
  entity,
  includes,
  filters,
  sorts,
  page,
  query,
  clearState,
  entityAlias,
  skipJSONExtension
) => {
  return async (dispatch, getState) => {
    const extension = skipJSONExtension ? '' : '.json';
    const url = buildUrl(`${entity}${extension}`, { includes, filters, sorts, page, query });
    dispatchActivePage(dispatch, page);
    await readEntityFromUrl(dispatch, getState, entity, url, null, clearState, '', '', entityAlias);
  };
};

const readEntityFromObject = ({
  entity,
  includes,
  filters,
  sorts,
  page,
  query,
  fields,
  token,
  clearState = true,
  entityAlias,
  basePath = '',
  host = '',
  skipJSONExtension = false,
}) => {
  return async (dispatch, getState) => {
    const {
      api: { activeFilters, activePage, activeSearch, activeSort },
    } = getState();
    filters = filters || activeFilters;
    sorts = sorts || activeSort;
    page = page || activePage;
    query = query || activeSearch;
    const extension = skipJSONExtension ? '' : '.json';
    const url = buildUrl(`${entity}${extension}`, {
      includes,
      filters,
      sorts,
      page,
      query,
      fields,
    });
    dispatchActivePage(dispatch, page);
    await readEntityFromUrl(dispatch, getState, entity, url, token, clearState, basePath, host, entityAlias);
  };
};

const dispatchActivePage = (dispatch, page) => {
  let number;
  if (page) {
    if (Number.isInteger(page)) {
      number = page;
    } else {
      number = page.number;
    }
  }
  if (number) {
    dispatch(setActivePage({ number }));
  }
};

const readEntityFromUrl = async (
  dispatch,
  getState,
  entity,
  url,
  token,
  clearState,
  basePath = '',
  host = '',
  entityAlias = undefined
) => {
  dispatch(loadEntities(entityAlias || entity, url));
  let data;
  if (token) {
    data = await loadExternalData(`${host}${basePath}${url}`, token, false);
    data.endpoint = url;
    dispatch(readLocal(data));
  } else {
    try {
      const read = readEndpoint(url);
      if (clearState) {
        await read(
          params => {
            if (params.type === 'API_READ') {
              dispatch(clearApiState(entityAlias || entity));
            }

            // tag objects directly from the request to differentiate them from included entities,
            // to account for scenarios where an object has a child relationship with the same entity type
            if (params.payload && typeof params.payload === 'object') {
              if (params.payload.data) {
                params.payload.data.forEach(obj => {
                  if (entityAlias) {
                    obj.type = entityAlias;
                  }
                  if (obj.meta) {
                    obj.meta.included = false;
                  } else {
                    obj.meta = { included: false };
                  }
                });
              }

              if (params.payload.included) {
                params.payload.included.forEach(obj => {
                  if (obj.meta) {
                    obj.meta.included = true;
                  } else {
                    obj.meta = { included: true };
                  }
                });
              }
            }

            dispatch(params);
          },
          () => {
            const endpointOverride = {};
            if (basePath) {
              endpointOverride.path = basePath;
            }
            if (host) {
              endpointOverride.host = host;
            }
            const state = cloneDeep(getState());
            state.api.endpoint = { ...state.api.endpoint, ...endpointOverride };
            return state;
          }
        );
      } else {
        dispatch(read);
      }
    } catch (e) {
      dispatch(displayError(e));
    }
  }
};

export const updateEntityAndRefreshAll = (entity, includes, filters, sorts, afterSave, successMessage) => {
  return async function (dispatch, getState) {
    const update = updateResource(entity.unwrap());
    const entityTypeForDisplay = capitalizeMessage(entity.unwrap().type);
    try {
      const response = await update(dispatch, getState);
      dispatch(clearMessages());
      // added timeout for elasticsearch to finish reindexing (which takes about a second)
      // in order to return updated list results
      if (successMessage !== false) {
        setTimeout(() => {
          dispatch(readEntity(entity.unwrap().type, includes, filters, sorts));
          dispatch(
            displaySuccessMessage(
              successMessage ||
                i18n.t('%s updated successfully!', { postProcess: 'sprintf', sprintf: [entityTypeForDisplay] })
            )
          );
        }, 1000);
      }
      if (typeof afterSave === 'function') {
        afterSave(dispatch, getState);
      }
      return response;
    } catch (e) {
      dispatch(displayError(e));
    }
  };
};

export const readExternalEntity = (host, token, entity, includes, filters, page) => {
  return async function (dispatch) {
    const url = buildUrl(`${host}${entity}.json`, { includes, filters, page });
    dispatch(loadEntities(entity, url));
    try {
      const data = await loadExternalData(url, token, false);
      // set endpoint so loadingEntities clear correctly
      data.endpoint = url;
      dispatch(readLocal(data));
    } catch (e) {
      dispatch(displayError(e));
    }
  };
};
